/**
 * channel-release-manager.ts
 *
 * Banded Session-Deterministic Canary Assignment with 24h TTL Pinning,
 * Purpose-Separated Canary HMAC Keying, and Urgent Rollback Pin Overrides.
 */

import { PrivacySanitizer } from './privacy-sanitizer.js';
import { ChannelRelease, SessionReleaseAssignment, CandidateChange } from './learning-contracts.js';

export interface ResolveReleaseParams {
  readonly tenantId?: string;
  readonly principalId: string;
  readonly sessionId: string;
  readonly channelId: string;
  readonly now?: number;
}

export class ChannelReleaseManager {
  private readonly releases = new Map<string, ChannelRelease>();
  private readonly sessionAssignments = new Map<string, SessionReleaseAssignment>();
  private readonly sessionTtlMs = 24 * 3600_000; // 24 hours TTL

  constructor(
    private readonly privacySanitizer: PrivacySanitizer = new PrivacySanitizer(),
    initialReleases?: readonly ChannelRelease[]
  ) {
    if (initialReleases) {
      for (const r of initialReleases) {
        this.releases.set(r.releaseId, r);
      }
    }
  }

  /**
   * Resolves release for a session deterministically and pins it for 24h unless rolled back.
   */
  resolveReleaseForSession(params: ResolveReleaseParams): {
    release: ChannelRelease;
    assignment: SessionReleaseAssignment;
    isCanary: boolean;
  } {
    const tenantId = params.tenantId || 'tenant-default';
    const now = params.now || Date.now();

    this.evictExpiredSessions(now);

    const routingKeyHash = this.privacySanitizer.generateKeyVersionedHmac('canary', `${params.sessionId}:${params.channelId}`, tenantId);

    // Check if unexpired pinned assignment exists
    const existing = this.sessionAssignments.get(routingKeyHash);
    if (existing && new Date(existing.expiresAt).getTime() > now) {
      const release = this.releases.get(existing.releaseId);
      // Safety Rule: If release was retracted/rolled back, override pin immediately
      if (release && release.isCurrentActive && !release.isRolledBack) {
        return { release, assignment: existing, isCanary: existing.isCanary };
      }
    }

    // Find active releases for channel
    const channelReleases = Array.from(this.releases.values())
      .filter((r) => r.channelId === params.channelId && r.tenantId === tenantId && r.isCurrentActive && !r.isRolledBack)
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());

    if (channelReleases.length === 0) {
      const fallbackRelease: ChannelRelease = {
        releaseId: `rel-default-${params.channelId}`,
        channelId: params.channelId,
        tenantId,
        promptVersion: '1.0.0',
        schemaVersion: '1.0.0',
        retrievalVersion: '1.0.0',
        policyVersion: '1.0.0',
        rootArtifactIds: [],
        rolloutPercent: 100,
        isCurrentActive: true,
        isRolledBack: false,
        startedAt: new Date(now).toISOString(),
      };
      this.releases.set(fallbackRelease.releaseId, fallbackRelease);
      channelReleases.push(fallbackRelease);
    }

    // Banded Canary Allocation (0..99)
    const hashInt = parseInt(routingKeyHash.slice(3, 11), 16) || (params.sessionId.length * 17);
    const assignedBand = hashInt % 100;

    const canaryCandidate = channelReleases.find((r) => r.rolloutPercent < 100 && r.rolloutPercent > 0);
    const baselineRelease = channelReleases.find((r) => r.rolloutPercent === 100) || channelReleases[0]!;

    let selectedRelease = baselineRelease;
    let isCanary = false;

    if (canaryCandidate && assignedBand < canaryCandidate.rolloutPercent) {
      selectedRelease = canaryCandidate;
      isCanary = true;
    }

    // Pin assignment for 24h
    const assignment: SessionReleaseAssignment = {
      assignmentId: `asgn-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      routingKeyHash,
      sessionId: params.sessionId,
      channelId: params.channelId,
      tenantId,
      releaseId: selectedRelease.releaseId,
      isCanary,
      assignedBand,
      assignedAt: new Date(now).toISOString(),
      expiresAt: new Date(now + this.sessionTtlMs).toISOString(),
      assignmentVersion: 1,
    };

    this.sessionAssignments.set(routingKeyHash, assignment);

    return {
      release: selectedRelease,
      assignment,
      isCanary,
    };
  }

  /**
   * Promotes an approved candidate into a staged or full ChannelRelease.
   */
  deployRelease(params: {
    channelId: string;
    tenantId?: string;
    candidate: CandidateChange;
    rolloutPercent: number;
    rootArtifactIds?: readonly string[];
  }): ChannelRelease {
    const tenantId = params.tenantId || 'tenant-default';
    const releaseId = `rel-${params.channelId}-${params.candidate.candidateVersion}-${Date.now()}`;

    const release: ChannelRelease = {
      releaseId,
      channelId: params.channelId,
      tenantId,
      promptVersion: params.candidate.changeType === 'prompt' ? params.candidate.candidateVersion : '1.0.0',
      schemaVersion: params.candidate.changeType === 'schema' ? params.candidate.candidateVersion : '1.0.0',
      retrievalVersion: params.candidate.changeType === 'retrieval' ? params.candidate.candidateVersion : '1.0.0',
      policyVersion: '1.0.0',
      candidateId: params.candidate.candidateId,
      rootArtifactIds: params.rootArtifactIds || params.candidate.evidenceArtifactIds,
      rolloutPercent: params.rolloutPercent,
      isCurrentActive: true,
      isRolledBack: false,
      startedAt: new Date().toISOString(),
    };

    if (params.rolloutPercent === 100) {
      for (const [id, rel] of this.releases.entries()) {
        if (rel.channelId === params.channelId && rel.tenantId === tenantId && rel.rolloutPercent === 100) {
          this.releases.set(id, { ...rel, isCurrentActive: false });
        }
      }
    }

    this.releases.set(releaseId, release);
    return release;
  }

  /**
   * 1-Click Atomic Rollback to LKG release.
   */
  rollbackRelease(releaseId: string, reason: string): { rolledBack: ChannelRelease; lkg?: ChannelRelease } {
    const target = this.releases.get(releaseId);
    if (!target) throw new Error(`Release ${releaseId} not found`);

    const rolledBack: ChannelRelease = {
      ...target,
      isCurrentActive: false,
      isRolledBack: true,
      rolledBackAt: new Date().toISOString(),
      rollbackReason: reason,
    };
    this.releases.set(releaseId, rolledBack);

    const history = Array.from(this.releases.values())
      .filter((r) => r.channelId === target.channelId && r.tenantId === target.tenantId && r.releaseId !== releaseId && !r.isRolledBack)
      .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());

    const lkg = history[0];
    if (lkg) {
      this.releases.set(lkg.releaseId, {
        ...lkg,
        isCurrentActive: true,
      });
    }

    // Invalidate affected session assignments
    for (const [key, assignment] of this.sessionAssignments.entries()) {
      if (assignment.releaseId === releaseId) {
        this.sessionAssignments.delete(key);
      }
    }

    return { rolledBack, lkg };
  }

  private evictExpiredSessions(now: number): void {
    for (const [key, assignment] of this.sessionAssignments.entries()) {
      if (new Date(assignment.expiresAt).getTime() <= now) {
        this.sessionAssignments.delete(key);
      }
    }
  }

  getReleases(): readonly ChannelRelease[] {
    return Array.from(this.releases.values());
  }

  getRelease(releaseId: string): ChannelRelease | undefined {
    return this.releases.get(releaseId);
  }
}
